import { useCallback, useEffect, useRef, useState } from 'react';
import { apiGet, apiPost } from '../game/api';
import { t, type Key } from '../i18n';
import { Icon } from './icons';
import type { ModeChoice } from './ModePicker';

export interface ChatLine {
  id: number;
  from: string;
  name: string;
  text: string;
  at: number;
  sys?: 'joined' | 'left' | 'kicked' | 'leader' | 'launch';
}

export interface GroupState {
  group: { id: string; leader: string; members: { publicId: string; name: string; online: boolean; leader: boolean }[]; pending: string[] } | null;
  invites: { groupId: string; from: string; size: number }[];
  launch: { seq: number; req: ModeChoice; at: number } | null;
}

/** Limite de taille d'un groupe pour un mode (mêmes règles que le serveur). */
export function groupLimit(m: ModeChoice): number {
  if (m.mode === 'ffa' || m.mode === 'forge') return 4;
  if (m.mode === 'survival') return 6;
  if (m.mode === 'tdm' || m.mode === 'dom') return { '2v2': 2, '4v4': 4, '8v8': 8 }[m.format];
  return 8;
}

/**
 * État du groupe interrogé toutes les 2,5 s (comptes connectés seulement). Les messages arrivent par
 * incréments (`since`) ; un nouveau lancement du chef est signalé une seule fois via `onLaunch`.
 */
export function useGroup(enabled: boolean, myPublicId: string | null, onLaunch?: (req: ModeChoice, seq: number) => void) {
  const [state, setState] = useState<GroupState>({ group: null, invites: [], launch: null });
  const [chat, setChat] = useState<ChatLine[]>([]);
  const since = useRef(0);
  const groupId = useRef<string | null>(null);
  const lastLaunch = useRef<number | null>(null);
  const launchCb = useRef(onLaunch);
  launchCb.current = onLaunch;

  const poll = useCallback(async () => {
    if (!enabled) return;
    try {
      const r = await apiGet<GroupState & { chat: ChatLine[] }>(`/api/group?since=${since.current}`);
      if (!r || !('invites' in r)) return;
      if ((r.group?.id ?? null) !== groupId.current) {
        // Changement de groupe : on repart d'un historique vide.
        groupId.current = r.group?.id ?? null;
        since.current = 0;
        setChat([]);
        lastLaunch.current = r.launch?.seq ?? 0;
        if (r.group) void poll();
      } else if (r.chat.length) {
        since.current = r.chat[r.chat.length - 1].id;
        setChat((c) => [...c, ...r.chat].slice(-60));
      }
      setState({ group: r.group, invites: r.invites, launch: r.launch });
      // Le premier relevé fixe la référence : un ancien lancement ne relance pas un membre.
      if (lastLaunch.current === null) lastLaunch.current = r.launch?.seq ?? 0;
      else if (r.launch && r.launch.seq > lastLaunch.current) {
        lastLaunch.current = r.launch.seq;
        const leader = r.group?.leader;
        if (leader && leader !== myPublicId && Date.now() - r.launch.at < 60_000) launchCb.current?.(r.launch.req, r.launch.seq);
      }
    } catch {
      /* réseau momentanément indisponible : prochain relevé */
    }
  }, [enabled, myPublicId]);

  useEffect(() => {
    if (!enabled) return;
    void poll();
    const id = window.setInterval(() => void poll(), 2500);
    return () => clearInterval(id);
  }, [enabled, poll]);

  return { state, chat, refresh: poll };
}

/** Panneau du menu : invitations reçues, membres, invitation d'amis en ligne, chat du groupe. */
export function GroupPanel({ me, group }: { me: { publicId: string }; group: ReturnType<typeof useGroup> }) {
  const { state, chat, refresh } = group;
  const [friends, setFriends] = useState<{ publicId: string; name: string; status: string; online: boolean }[]>([]);
  const [msg, setMsg] = useState('');
  const g = state.group;
  const leader = !g || g.leader === me.publicId;

  useEffect(() => {
    void apiGet<{ friends: typeof friends }>('/api/friends')
      .then((r) => setFriends(r.friends ?? []))
      .catch(() => {});
  }, [g?.members.length]);

  const act = async (path: string, body: Record<string, unknown>, prefix?: string) => {
    const r = await apiPost<{ result?: string }>(path, body);
    setMsg(prefix && r.data.result ? t(`${prefix}${r.data.result}` as Key) : '');
    await refresh();
  };
  const inGroup = new Set(g?.members.map((m) => m.publicId) ?? []);
  const invitable = friends.filter((f) => f.status === 'friend' && !inGroup.has(f.publicId));

  return (
    <section className="plate group-panel" data-testid="group-panel" aria-labelledby="group-title">
      <h2 className="plate-title" id="group-title">
        {t('group')}
        {g && <span className="badge num">{g.members.length} / 8</span>}
        <span className="grow" />
        {g && (
          <button type="button" className="btn btn-ghost btn-small" onClick={() => act('/api/group/leave', {})}>
            <Icon name="exit" size={14} />
            {t('groupLeave')}
          </button>
        )}
      </h2>
      {state.invites.map((inv) => (
        <div key={inv.groupId} className="group-invite">
          <Icon name="mail" size={18} />
          <span className="grow">{t('groupInvitedBy', { name: inv.from, n: inv.size })}</span>
          <button type="button" className="btn btn-small btn-accept" onClick={() => act('/api/group/accept', { groupId: inv.groupId }, 'gacc_')} data-testid="group-accept">
            {t('accept')}
          </button>
          <button type="button" className="btn btn-small btn-ghost" onClick={() => act('/api/group/decline', { groupId: inv.groupId })}>
            {t('decline')}
          </button>
        </div>
      ))}
      {g ? (
        <ul className="group-members">
          {g.members.map((m) => (
            <li key={m.publicId} className={m.publicId === me.publicId ? 'me' : ''}>
              <span className="member-badge" aria-hidden>
                <Icon name={m.leader ? 'crown' : 'profile'} size={16} />
              </span>
              <span className={`dot ${m.online ? 'on' : ''}`} role="img" aria-label={m.online ? t('online') : t('offline')} />
              <span className="member-name">{m.name}</span>
              {m.leader && <span className="badge badge-accent">{t('groupLeader')}</span>}
              {leader && m.publicId !== me.publicId && (
                <span className="member-actions">
                  <button type="button" className="btn btn-ghost btn-small" onClick={() => act('/api/group/promote', { publicId: m.publicId })}>
                    {t('groupPromote')}
                  </button>
                  <button type="button" className="btn btn-ghost btn-small" onClick={() => act('/api/group/kick', { publicId: m.publicId })}>
                    {t('kick')}
                  </button>
                </span>
              )}
            </li>
          ))}
          {g.pending.map((n) => (
            <li key={`p-${n}`} className="pending">
              <span className="member-badge" aria-hidden>
                <Icon name="mail" size={16} />
              </span>
              <span className="member-name">{n}</span>
              <span className="wip">{t('groupPending')}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="wip">{t('groupEmpty')}</p>
      )}
      {leader && invitable.length > 0 && (
        <div className="group-invitable">
          <span className="eyebrow">{t('groupInviteFriends')}</span>
          {invitable.map((f) => (
            <button key={f.publicId} type="button" className="chip" onClick={() => act('/api/group/invite', { publicId: f.publicId }, 'ginv_')} data-testid={`group-invite-${f.name}`}>
              <span className={`dot ${f.online ? 'on' : ''}`} aria-hidden />
              <Icon name="plus" size={14} />
              {f.name}
            </button>
          ))}
        </div>
      )}
      {msg && <p className="form-msg">{msg}</p>}
      {g && <GroupChat me={me} chat={chat} onSent={refresh} />}
    </section>
  );
}

/** Chat textuel du groupe : filtré et limité côté serveur ; signalement et blocage par message. */
export function GroupChat({ me, chat, onSent, compact }: { me: { publicId: string }; chat: ChatLine[]; onSent: () => void; compact?: boolean }) {
  const [text, setText] = useState('');
  const [msg, setMsg] = useState('');
  const [menu, setMenu] = useState<number | null>(null);
  const list = useRef<HTMLDivElement>(null);
  useEffect(() => {
    list.current?.scrollTo({ top: list.current.scrollHeight });
  }, [chat.length]);

  const send = async () => {
    if (!text.trim()) return;
    const r = await apiPost<{ result?: string }>('/api/group/chat', { text });
    if (r.data.result === 'ok') {
      setText('');
      setMsg('');
      onSent();
    } else setMsg(t(`chat_${r.data.result ?? 'error'}` as Key));
  };
  const report = async (line: ChatLine, reason: string) => {
    const r = await apiPost<{ result?: string }>('/api/report', { publicId: line.from, reason, messageId: line.id });
    setMsg(t(`report_${r.data.result ?? 'error'}` as Key));
    setMenu(null);
  };
  const block = async (line: ChatLine) => {
    await apiPost('/api/block', { publicId: line.from });
    setMsg(t('blockedDone', { name: line.name }));
    setMenu(null);
    onSent();
  };

  return (
    <div className={`group-chat ${compact ? 'compact' : ''}`}>
      <div className="chat-list" ref={list} role="log" aria-live="polite" data-testid="group-chat">
        {chat.map((c) =>
          c.sys ? (
            <div key={c.id} className="chat-sys">
              {t(`gsys_${c.sys}` as Key, { name: c.name })}
            </div>
          ) : (
            <div key={c.id} className={`chat-line ${c.from === me.publicId ? 'mine' : ''}`}>
              <strong>{c.name}</strong> {c.text}
              {c.from !== me.publicId && (
                <button type="button" className="chat-more" aria-label={t('groupReport')} aria-expanded={menu === c.id} onClick={() => setMenu(menu === c.id ? null : c.id)}>
                  <Icon name="alert" size={14} />
                </button>
              )}
              {menu === c.id && (
                <span className="chat-menu">
                  {(['insult', 'spam', 'cheat', 'other'] as const).map((r) => (
                    <button key={r} className="chip" onClick={() => report(c, r)}>
                      {t(`reason_${r}` as Key)}
                    </button>
                  ))}
                  <button className="chip" onClick={() => block(c)}>
                    {t('block')}
                  </button>
                </span>
              )}
            </div>
          ),
        )}
      </div>
      <form
        className="chat-form"
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        <input value={text} maxLength={200} placeholder={t('groupPlaceholder')} onChange={(e) => setText(e.target.value)} aria-label={t('groupPlaceholder')} />
        <button className="btn btn-small" type="submit">
          <Icon name="chat" size={14} />
          {t('groupSend')}
        </button>
      </form>
      {msg && <p className="form-msg">{msg}</p>}
    </div>
  );
}

/** En partie : bouton discret du chat de groupe, avec annonce d'un lancement du chef. */
export function GroupDock({
  me,
  group,
  joinedSeq,
  onJoinLaunch,
}: {
  me: { publicId: string };
  group: ReturnType<typeof useGroup>;
  /** Dernier lancement déjà suivi : pas de bouton « Rejoindre » pour la partie en cours. */
  joinedSeq: number;
  onJoinLaunch: (req: ModeChoice, seq: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState(0);
  const { state, chat, refresh } = group;
  if (!state.group) return null;
  const unread = chat.filter((c) => c.id > seen && !c.sys && c.from !== me.publicId).length;
  const launch = state.launch && state.launch.seq > joinedSeq && state.group.leader !== me.publicId && Date.now() - state.launch.at < 60_000 ? state.launch : null;
  return (
    <div className={`group-dock ${open ? 'open' : ''}`}>
      <button
        className="chip"
        onClick={() => {
          setOpen(!open);
          setSeen(chat[chat.length - 1]?.id ?? 0);
        }}
        data-testid="group-dock"
      >
        <Icon name="chat" size={16} />
        {t('group')}
        {unread > 0 && !open && <span className="nav-badge num">{unread}</span>}
      </button>
      {launch && (
        <button className="chip chip-on" onClick={() => onJoinLaunch(launch.req, launch.seq)}>
          {t('groupJoinLaunch')}
        </button>
      )}
      {open && <GroupChat me={me} chat={chat} onSent={refresh} compact />}
    </div>
  );
}
